import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Star, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { SectionHeading } from "@/components/ui-bits";

function Stars({ value, size = "w-4 h-4" }) {
  return (
    <div className="inline-flex gap-0.5" aria-label={`${value} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} className={`${size} ${i <= value ? "text-ei-golddark fill-ei-golddark" : "text-ei-mist"}`} />
      ))}
    </div>
  );
}

export function FeaturedReview() {
  const [review, setReview] = useState(null);
  useEffect(() => {
    api
      .get("/reviews")
      .then(({ data }) => {
        const five = (data.reviews || []).filter((r) => r.rating === 5);
        if (five.length) setReview(five[0]);
      })
      .catch(() => {});
  }, []);
  if (!review) return null;
  return (
    <div
      data-testid="hero-featured-review"
      className="mt-8 max-w-xl rounded-md border border-ei-white/15 bg-ei-white/[0.05] p-4 sm:p-5 ei-rise"
    >
      <Stars value={5} />
      <p className="mt-2 text-sm text-ei-white/90 leading-relaxed italic line-clamp-3">
        &ldquo;{review.review}&rdquo;
      </p>
      <p className="mt-2 text-xs font-semibold text-ei-white/60">
        — {review.name}
        {review.company ? `, ${review.company}` : ""}
      </p>
    </div>
  );
}

function StarInput({ value, onChange }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex gap-1" data-testid="review-rating" role="radiogroup" aria-label="Star rating">
      {[1, 2, 3, 4, 5].map((i) => (
        <button
          type="button"
          key={i}
          onMouseEnter={() => setHover(i)}
          onMouseLeave={() => setHover(0)}
          onClick={() => onChange(i)}
          aria-label={`${i} star${i > 1 ? "s" : ""}`}
          aria-pressed={i === value}
          data-testid={`review-star-${i}`}
          className="p-1 rounded-sm"
        >
          <Star className={`w-7 h-7 transition-colors ${i <= (hover || value) ? "text-ei-golddark fill-ei-golddark" : "text-ei-mist"}`} />
        </button>
      ))}
    </div>
  );
}

export function ReviewList({ reviews, dark = false }) {
  if (!reviews.length) {
    return <p className={`text-sm ${dark ? "text-ei-white/60" : "text-ei-graphite/60"}`} data-testid="reviews-empty">No reviews yet — be the first to leave one.</p>;
  }
  const single = reviews.length === 1;
  return (
    <div className={single ? "max-w-xl mx-auto" : "grid sm:grid-cols-2 gap-5"} data-testid="reviews-list">
      {reviews.map((r) => (
        <div key={r.id} className={`rounded-md p-5 ${dark ? "border border-ei-white/15 bg-ei-white/[0.04]" : "border border-ei-mist bg-white"}`} data-testid={`review-item-${r.id}`}>
          <Stars value={r.rating} />
          <p className={`mt-3 text-sm leading-relaxed ${dark ? "text-ei-white/85" : "text-ei-graphite/90"}`}>“{r.review}”</p>
          <p className={`mt-3 text-sm font-semibold ${dark ? "text-ei-white" : "text-ei-navy"}`}>
            {r.name}
            {r.company ? <span className={`font-normal ${dark ? "text-ei-white/60" : "text-ei-graphite/60"}`}> · {r.company}</span> : null}
          </p>
        </div>
      ))}
    </div>
  );
}

function useReviews() {
  const [data, setData] = useState({ reviews: [], count: 0, average: 0 });
  const [loading, setLoading] = useState(true);
  const load = async () => {
    try {
      const r = await api.get("/reviews");
      setData(r.data);
    } catch {
      // non-blocking: reviews section simply stays empty
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);
  return { ...data, loading, reload: load };
}

export function ReviewsShowcase({ dark = false }) {
  const { reviews, average, count, loading } = useReviews();
  return (
    <div>
      {count > 0 && (
        <div className="flex items-center justify-center gap-3 mb-8" data-testid="reviews-summary">
          <Stars value={Math.round(average)} size="w-5 h-5" />
          <span className={`text-sm ${dark ? "text-ei-white/80" : "text-ei-graphite/80"}`}>
            <strong className={`tnum ${dark ? "text-ei-white" : "text-ei-navy"}`}>{average}</strong> from {count} review{count !== 1 ? "s" : ""}
          </span>
        </div>
      )}
      {loading ? (
        <div className="flex justify-center py-6"><Loader2 className={`w-6 h-6 animate-spin ${dark ? "text-ei-white" : "text-ei-navy"}`} /></div>
      ) : (
        <ReviewList reviews={reviews.slice(0, 4)} dark={dark} />
      )}
      <div className="mt-8 text-center">
        <Link to="/about#reviews" className={`inline-flex items-center gap-2 font-display text-sm font-bold px-5 py-3 rounded-sm transition-colors min-h-[44px] ${dark ? "text-ei-white border border-ei-white/30 hover:border-ei-white" : "text-ei-navy border border-ei-navy/25 hover:border-ei-navy"}`} data-testid="leave-review-cta">
          Leave a review <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}

export function ReviewsSection() {
  const { reviews, average, count, loading, reload } = useReviews();
  const [form, setForm] = useState({ name: "", company: "", rating: 0, review: "", company_website: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.rating) { toast.error("Please select a star rating"); return; }
    if (form.name.trim().length < 2) { toast.error("Please enter your name"); return; }
    if (form.review.trim().length < 5) { toast.error("Please write a short review"); return; }
    setSubmitting(true);
    try {
      await api.post("/reviews", {
        name: form.name.trim(),
        company: form.company.trim() || null,
        rating: form.rating,
        review: form.review.trim(),
        company_website: form.company_website,
      });
      toast.success("Thanks — your review has been submitted and will appear once approved");
      setForm({ name: "", company: "", rating: 0, review: "", company_website: "" });
      reload();
    } catch (err) {
      toast.error(formatApiError(err, "Could not submit your review"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div id="reviews" className="scroll-mt-24" data-testid="reviews-section">
      <SectionHeading eyebrow="Customer reviews" title="What contractors say" />
      {count > 0 && (
        <div className="mt-4 flex items-center gap-3" data-testid="reviews-average">
          <Stars value={Math.round(average)} size="w-5 h-5" />
          <span className="text-sm text-ei-graphite/80"><strong className="text-ei-navy tnum">{average}</strong> from {count} review{count !== 1 ? "s" : ""}</span>
        </div>
      )}
      <div className="mt-8 grid lg:grid-cols-12 gap-10 items-start">
        <form onSubmit={submit} className="lg:col-span-5 border border-ei-mist rounded-md p-6 bg-ei-white" data-testid="review-form">
          <h3 className="font-display font-semibold text-ei-navy">Leave a review</h3>
          <p className="mt-1 text-sm text-ei-graphite/70">Your review is checked by our team before it appears on the site.</p>
          <div className="mt-4">
            <label className="block text-xs font-semibold text-ei-navy mb-1.5">Your rating</label>
            <StarInput value={form.rating} onChange={(v) => setForm((f) => ({ ...f, rating: v }))} />
          </div>
          <div className="mt-4">
            <label className="block text-xs font-semibold text-ei-navy mb-1.5">Name</label>
            <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="w-full min-h-[44px] rounded-sm border border-ei-mist px-3 py-2 text-sm focus:border-ei-navy outline-none" data-testid="review-name" maxLength={80} />
          </div>
          <div className="mt-4">
            <label className="block text-xs font-semibold text-ei-navy mb-1.5">Company <span className="text-ei-graphite/50 font-normal">(optional)</span></label>
            <input value={form.company} onChange={(e) => setForm((f) => ({ ...f, company: e.target.value }))} className="w-full min-h-[44px] rounded-sm border border-ei-mist px-3 py-2 text-sm focus:border-ei-navy outline-none" data-testid="review-company" maxLength={120} />
          </div>
          <div className="mt-4">
            <label className="block text-xs font-semibold text-ei-navy mb-1.5">Review</label>
            <textarea rows={4} value={form.review} onChange={(e) => setForm((f) => ({ ...f, review: e.target.value }))} className="w-full rounded-sm border border-ei-mist px-3 py-2 text-sm focus:border-ei-navy outline-none" data-testid="review-text" maxLength={1500} />
          </div>
          <input type="text" tabIndex={-1} autoComplete="off" value={form.company_website} onChange={(e) => setForm((f) => ({ ...f, company_website: e.target.value }))} className="hidden" aria-hidden="true" data-testid="review-hp" />
          <button type="submit" disabled={submitting} className="mt-5 w-full font-display text-sm font-bold bg-ei-navy text-ei-white px-5 py-3 rounded-sm hover:bg-ei-ink transition-colors min-h-[44px] inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="review-submit">
            {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</> : "Submit review"}
          </button>
        </form>
        <div className="lg:col-span-7">
          {loading ? (
            <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-ei-navy" /></div>
          ) : (
            <ReviewList reviews={reviews} />
          )}
        </div>
      </div>
    </div>
  );
}
